/**
 * 지출품의 공통 워크스페이스
 * - register: 작성·저장·승인요청
 * - approve: 승인요청 건 승인/반려
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import {
  FormArea,
  CollapsibleSearchPanel,
  CollapsibleSectionPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import IncomeStatementDialog from 'components/expense/IncomeStatementDialog';
import { loadDepts, filterDepts } from 'master/deptStore';
import { loadOrSeedProjects } from 'budget/budgetProjectStore';
import { loadSubjectsByFilter } from 'budget/budgetStructureStore';
import {
  loadExpenseBalanceRows,
  BALANCE_SEARCH_CONFIG,
  balanceLookupColumns,
  applyBalanceFields,
} from 'expense/expenseLoadUtils';
import {
  EXPENSE_STATUS,
  EXPENSE_STATUS_OPTIONS,
  PROPOSAL_TYPE,
  PROPOSAL_TYPE_OPTIONS,
  proposalTypeLabel,
  expenseStatusLabel,
  createProposalUid,
  createProposalDetailUid,
  emptyProposalMaster,
  emptyProposalVendorRow,
  loadProposalBundle,
  saveProposalBundle,
  ensureProposalSeedImported,
  nextProposalNo,
  filterProposalsForRegister,
  filterProposalsForApprove,
  requestProposalApproval,
  approveProposal,
  rejectProposal,
  todayYmd,
} from 'expense/expenseStore';
import { loadStatementsByProposal } from 'expense/expenseIncomeStore';

const LIST_GRID_ID = 'expense-proposal-list';
const VENDOR_GRID_ID = 'expense-proposal-vendor';
/** 엑셀 내보내기용 마스터(폼) — 품의목록 그리드는 등록하지 않음 */
const MASTER_EXPORT_ID = 'expense-proposal-master';

// 영역별 높이 — 목록을 펼쳐도 서로 줄이지 않고 아래로 늘어나므로 각자 높이를 갖는다
const VENDOR_PANEL_H = '300px'; // 최소 높이(남는 공간이 있으면 더 늘어남)
const LIST_PANEL_H = '340px';

/**
 * 세로 흐름 — 각 영역은 자기 높이를 갖고, 목록을 펼치면 위 영역을 줄이지 않고
 * 아래로 늘어나며 화면이 스크롤된다 (형제 간 높이 분배 아님).
 */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${hoverExpandScrollbarCss}

  > * {
    flex-shrink: 0;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || '0 0 auto'};
  height: ${(p) => p.$height || 'auto'};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

/** 버튼 유무와 무관하게 높이 고정 — 추가/선택 시 그리드 영역이 밀리지 않도록 */
const MasterToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  min-height: 34px;
  box-sizing: border-box;
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const BalanceBtn = styled.button`
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.xs};
  padding: 3px 8px;
  min-height: 26px;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  &:disabled {
    background: ${theme.colors.background.light};
    color: ${theme.colors.text.secondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/**
 * 열 슬롯 폭 — 행이 달라도 각 열의 「시작점」을 맞추기 위한 셀 폭.
 * 입력 폭(width)은 값 길이에 맞춰 슬롯보다 짧게 둘 수 있다 (끝점은 열마다 달라짐).
 */
const SEARCH_SLOT_W = ['230px', '280px', '330px'];
/** 품의일 From/To — 'YYYY-MM-DD' 한 줄에 맞춘 폭 */
const SEARCH_DATE_W = '116px';

const MASTER_SLOT_W = ['210px', '230px', '290px', '290px', '200px', '200px'];
/** 적요는 재원+현액 2개 열을 합쳐 사용 (200 + gap 12 + 200) */
const MASTER_SLOT_W_REMARK = '412px';
const MASTER_LABEL_W = '64px';

function emptySearch(partial = {}) {
  return {
    searchFiscalYear: getCurrentFiscalYear(),
    searchStatus: '',
    searchDeptCode: '',
    searchDeptName: '',
    searchDeptDisp: '',
    searchProjectCode: '',
    searchProjectName: '',
    searchProjectDisp: '',
    searchSubjectCode: '',
    searchSubjectName: '',
    searchSubjectDisp: '',
    searchDateFrom: '',
    searchDateTo: '',
    searchRemark: '',
    ...partial,
  };
}

/** 멀티 선택 요약 — 필드에는 '첫 항목 외 N건', 전체 목록은 tooltip */
function summarizeSelection(names = []) {
  const list = (names || []).map((s) => String(s || '').trim()).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return `${list[0]} 외 ${list.length - 1}건`;
}

function selectionTooltip(joinedNames, emptyText) {
  const list = String(joinedNames || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!list.length) return emptyText;
  if (list.length === 1) return list[0];
  return `선택 ${list.length}건\n${list.map((n) => `· ${n}`).join('\n')}`;
}

function emptyMasterForm(partial = {}) {
  return emptyProposalMaster({
    proposalDate: todayYmd(),
    ...partial,
  });
}

/** 조회조건 키 — 마스터 선택/reset 시 덮어쓰지 않음 */
const SEARCH_FIELD_KEYS = [
  'searchFiscalYear',
  'searchStatus',
  'searchDeptCode',
  'searchDeptName',
  'searchDeptDisp',
  'searchProjectCode',
  'searchProjectName',
  'searchProjectDisp',
  'searchSubjectCode',
  'searchSubjectName',
  'searchSubjectDisp',
  'searchDateFrom',
  'searchDateTo',
  'searchRemark',
];

function pickSearchValues(values = {}) {
  const out = {};
  SEARCH_FIELD_KEYS.forEach((k) => {
    out[k] = values[k] ?? '';
  });
  return out;
}

/**
 * @param {{ screenMode?: 'register'|'approve' }} props
 */
function ExpenseProposalWorkspaceInner({ screenMode = 'register' }) {
  const isApprove = screenMode === 'approve';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const scrollbarHoverProps = useScrollbarHoverProps();

  const searchDefaults = useMemo(
    () =>
      emptySearch({
        // 등록: 전체 · 승인: 승인요청
        searchStatus: isApprove ? EXPENSE_STATUS.REQUESTED : '',
      }),
    [isApprove]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...searchDefaults,
      ...emptyMasterForm({ fiscalYear: searchDefaults.searchFiscalYear }),
    },
  });
  const { getValues, reset, setValue, control } = methods;

  const listRef = useRef(null);
  const vendorRef = useRef(null);
  const [selectedUid, setSelectedUid] = useState('');
  const selectedUidRef = useRef('');
  const [bundleVendors, setBundleVendors] = useState([]);
  const [balanceLookupOpen, setBalanceLookupOpen] = useState(false);
  const [incomeStmtOpen, setIncomeStmtOpen] = useState(false);
  /** 소득명세 건수 — 팝업 저장·품의 선택 시 갱신 */
  const [incomeStmtCount, setIncomeStmtCount] = useState(0);
  const [searchLookup, setSearchLookup] = useState({ open: false, type: '' });
  const [listCount, setListCount] = useState(0);

  const masterStatus = useWatch({ control, name: 'status' });
  const proposalTypeWatch = useWatch({ control, name: 'proposalType' });
  const proposalAmtWatch = useWatch({ control, name: 'proposalAmt' });
  const proposalNoWatch = useWatch({ control, name: 'proposalNo' });
  const searchFiscalYearWatch = useWatch({ control, name: 'searchFiscalYear' });
  // 멀티 선택 tooltip 갱신용
  const searchDeptNameWatch = useWatch({ control, name: 'searchDeptName' });
  const searchProjectNameWatch = useWatch({ control, name: 'searchProjectName' });
  const searchSubjectNameWatch = useWatch({ control, name: 'searchSubjectName' });
  const masterReadonly = isApprove || (masterStatus && masterStatus !== EXPENSE_STATUS.DRAFT && masterStatus !== EXPENSE_STATUS.REJECTED);

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (isApprove) return '승인 화면에서는 추가할 수 없습니다.';
      if (id === VENDOR_GRID_ID && !selectedUidRef.current) {
        return '품의를 선택하거나 신규 추가하세요.';
      }
      return null;
    },
    guardDelete: () => (isApprove ? '승인 화면에서는 삭제할 수 없습니다.' : null),
  });

  const dimsFromSearch = useCallback(() => {
    const v = getValues();
    return {
      tenantCode,
      fiscalYear: String(v.searchFiscalYear || '').trim(),
    };
  }, [getValues, tenantCode]);

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '품의번호', field: 'proposalNo', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
      hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 120 }),
      hiCol.roNumberColumn({ header: '품의금액', field: 'proposalAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '품의일', field: 'proposalDate', size: 100, align: 'center' }),
    ],
    []
  );

  const vendorColumns = useMemo(
    () => [
      hiCol.edTextColumn({ header: '거래처코드', field: 'vendorCode', size: 100, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '거래처명', field: 'vendorName', size: 140, editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '사업자번호', field: 'businessNo', size: 110, editable: !masterReadonly }),
      hiCol.edNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right', editable: !masterReadonly }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 160, editable: !masterReadonly }),
    ],
    [masterReadonly]
  );

  const listGrid = useHiGridHook({
    gridRef: listRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const vendorGrid = useHiGridHook({
    gridRef: vendorRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: !isApprove && !masterReadonly,
    confirmCheck: !isApprove && !masterReadonly,
    defaultRow: emptyProposalVendorRow(),
  });

  const enrichListRow = useCallback(
    (row) => ({
      ...row,
      statusNm: expenseStatusLabel(row.status),
      proposalTypeNm: proposalTypeLabel(row.proposalType),
    }),
    []
  );

  const refreshIncomeStmtCount = useCallback(
    (uid, fy) => {
      const proposalUid = uid || selectedUidRef.current;
      const fiscalYear = String(fy || getValues('searchFiscalYear') || '').trim();
      if (!proposalUid || !fiscalYear || !tenantCode) {
        setIncomeStmtCount(0);
        return;
      }
      setIncomeStmtCount(
        loadStatementsByProposal({ tenantCode, fiscalYear }, proposalUid).length
      );
    },
    [getValues, tenantCode]
  );

  const loadMasterToForm = useCallback(
    (row) => {
      const searchKeep = pickSearchValues(getValues());
      if (!row) {
        reset({
          ...searchKeep,
          ...emptyMasterForm({ fiscalYear: searchKeep.searchFiscalYear }),
        });
        vendorRef.current?.setData?.([]);
        setIncomeStmtCount(0);
        return;
      }
      reset({
        ...searchKeep,
        ...row,
        statusNm: expenseStatusLabel(row.status),
      });
      const vendors = (bundleVendors || []).filter(
        (v) => v.proposalUid === row.proposalUid && v.mod !== 'D'
      );
      vendorRef.current?.setData?.(vendors);
      refreshIncomeStmtCount(row.proposalUid, searchKeep.searchFiscalYear || row.fiscalYear);
    },
    [reset, getValues, bundleVendors, refreshIncomeStmtCount]
  );

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    // 품의내역 Excel 시드 병합 (회계연도 2026 · 미존재 UID만)
    if (!isApprove) {
      await ensureProposalSeedImported(dims);
    }
    const v = getValues();
    const filter = {
      status: String(v.searchStatus || '').trim() || undefined,
      deptCode: String(v.searchDeptCode || '').trim() || undefined,
      deptName: String(v.searchDeptName || '').trim() || undefined,
      projectCode: String(v.searchProjectCode || '').trim() || undefined,
      projectName: String(v.searchProjectName || '').trim() || undefined,
      subjectCode: String(v.searchSubjectCode || '').trim() || undefined,
      subjectName: String(v.searchSubjectName || '').trim() || undefined,
      dateFrom: String(v.searchDateFrom || '').trim() || undefined,
      dateTo: String(v.searchDateTo || '').trim() || undefined,
      remark: String(v.searchRemark || '').trim() || undefined,
    };
    const bundle = await loadProposalBundle(dims);
    let masters = isApprove
      ? filterProposalsForApprove(bundle.masters, filter)
      : filterProposalsForRegister(bundle.masters, filter);
    masters = masters.map(enrichListRow);
    setBundleVendors(bundle.vendors);
    listRef.current?.setData?.(masters);
    setListCount(masters.length);
    const first = masters[0];
    const uid = first?.proposalUid || '';
    setSelectedUid(uid);
    loadMasterToForm(first);
  }, [dimsFromSearch, getValues, isApprove, enrichListRow, loadMasterToForm]);

  useEffect(() => {
    reset({
      ...searchDefaults,
      ...emptyMasterForm({ fiscalYear: searchDefaults.searchFiscalYear }),
    });
  }, [reset, searchDefaults]);

  const onListSelect = useCallback(
    (row) => {
      const uid = row?.proposalUid || '';
      setSelectedUid(uid);
      loadMasterToForm(row);
    },
    [loadMasterToForm]
  );

  useEffect(() => {
    listGrid.onRowClicked = ({ data }) => onListSelect(data);
    listGrid.onSelectRow = ({ data }) => onListSelect(data);
  }, [listGrid, onListSelect]);

  const collectVendorsFromGrid = useCallback(() => {
    const rows = vendorRef.current?.getData?.() || [];
    const uid = selectedUidRef.current;
    return rows
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        proposalUid: uid,
        detailUid: r.detailUid || createProposalDetailUid(),
      }));
  }, []);

  const handleNew = useCallback(async () => {
    if (isApprove) return;
    const uid = createProposalUid();
    const dims = dimsFromSearch();
    const bundle = await loadProposalBundle(dims);
    const row = enrichListRow(
      emptyProposalMaster({
        proposalUid: uid,
        proposalNo: nextProposalNo(bundle.masters),
        fiscalYear: dims.fiscalYear,
        proposalDate: todayYmd(),
        status: EXPENSE_STATUS.DRAFT,
        registerDate: todayYmd(),
        mod: 'A',
      })
    );
    setSelectedUid(uid);
    reset({ ...pickSearchValues(getValues()), ...row });
    vendorRef.current?.setData?.([]);
    setIncomeStmtCount(0);
  }, [isApprove, dimsFromSearch, enrichListRow, reset, getValues]);

  const handleSave = useCallback(async () => {
    if (isApprove) {
      showAlert('승인 화면에서는 저장 대신 승인/반려를 사용하세요.');
      return;
    }
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 확인하세요.');
      return;
    }
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createProposalUid();
      selectedUidRef.current = uid;
      setSelectedUid(uid);
    }

    const form = getValues();
    const bundle = await loadProposalBundle(dims);
    const vendorsForUid = collectVendorsFromGrid();
    let masters = [...(bundle.masters || [])];
    const idx = masters.findIndex((m) => m.proposalUid === uid);
    const masterRow = {
      ...emptyProposalMaster(),
      ...form,
      proposalUid: uid,
      proposalNo: form.proposalNo || nextProposalNo(masters),
      fiscalYear: dims.fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      status: form.status || EXPENSE_STATUS.DRAFT,
      mod: idx >= 0 ? 'U' : 'A',
    };
    if (idx >= 0) masters[idx] = masterRow;
    else masters.push(masterRow);

    let allVendors = (bundle.vendors || []).filter((v) => v.proposalUid !== uid);
    allVendors = [...allVendors, ...vendorsForUid];

    const saved = await saveProposalBundle(dims, masters, allVendors);
    setBundleVendors(saved.vendors);
    listRef.current?.setData?.(saved.masters.filter((m) => m.mod !== 'D').map(enrichListRow));
    showAlert('저장했습니다.', 'success');
  }, [isApprove, dimsFromSearch, getValues, collectVendorsFromGrid, enrichListRow]);

  const handleRequest = useCallback(async () => {
    if (isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인요청할 품의를 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== EXPENSE_STATUS.DRAFT && form.status !== EXPENSE_STATUS.REJECTED) {
      showAlert('작성중·반려 건만 승인요청할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('선택한 품의를 승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadProposalBundle(dims);
    const { masters, vendors } = requestProposalApproval(bundle.masters, bundle.vendors, uid);
    await saveProposalBundle(dims, masters, vendors);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [isApprove, getValues, handleSave, dimsFromSearch, handleSearch]);

  const handleApprove = useCallback(async () => {
    if (!isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('승인할 건을 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== EXPENSE_STATUS.REQUESTED) {
      showAlert('승인요청 상태인 건만 승인할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadProposalBundle(dims);
    const { masters, vendors } = approveProposal(bundle.masters, bundle.vendors, uid);
    await saveProposalBundle(dims, masters, vendors);
    await handleSearch();
    showAlert('승인했습니다.', 'success');
  }, [isApprove, getValues, dimsFromSearch, handleSearch]);

  const handleReject = useCallback(async () => {
    if (!isApprove) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('반려할 건을 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== EXPENSE_STATUS.REQUESTED) {
      showAlert('승인요청 상태인 건만 반려할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadProposalBundle(dims);
    const { masters, vendors } = rejectProposal(bundle.masters, bundle.vendors, uid);
    await saveProposalBundle(dims, masters, vendors);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isApprove, getValues, dimsFromSearch, handleSearch]);

  const dispatchVendorAdd = useCallback(async () => {
    if (isApprove || masterReadonly) return;
    if (!selectedUidRef.current) {
      showAlert('품의를 먼저 선택하거나 신규 추가하세요.');
      return;
    }
    const rows = vendorRef.current?.getData?.() || [];
    vendorRef.current?.setData?.([
      ...rows,
      emptyProposalVendorRow({
        detailUid: createProposalDetailUid(),
        proposalUid: selectedUidRef.current,
        mod: 'A',
      }),
    ]);
  }, [isApprove, masterReadonly]);

  const balanceLookupConfig = useMemo(() => {
    const dims = dimsFromSearch();
    const rows = loadExpenseBalanceRows(tenantCode, dims.fiscalYear);
    return {
      title: '현액 조회 (지출)',
      rows,
      columns: balanceLookupColumns(),
      enableSearch: true,
      searchConfig: BALANCE_SEARCH_CONFIG,
    };
  }, [dimsFromSearch, tenantCode]);

  const applyBalance = useCallback(
    (row) => {
      const r = Array.isArray(row) ? row[0] : row;
      if (!r) return true;
      applyBalanceFields(setValue, r);
      setValue('allocAmt', r.balanceAmt != null ? Number(r.balanceAmt) : Number(r.confirmedAmt) || 0);
      return true;
    },
    [setValue]
  );

  const searchLookupConfig = useMemo(() => {
    const fy = String(searchFiscalYearWatch || getCurrentFiscalYear()).trim();
    if (searchLookup.type === 'dept') {
      const rows = filterDepts(loadDepts(tenantCode), { useYn: 'Y' });
      return {
        title: '부서 조회 (검색)',
        rows: rows.map((r) => ({ ...r, id: r.deptUid || r.deptCode })),
        columns: [
          hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 100 }),
          hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['deptCode', 'deptName'] },
      };
    }
    if (searchLookup.type === 'project') {
      const loaded = loadOrSeedProjects({ tenantCode, fiscalYear: fy }) || [];
      const rows = (Array.isArray(loaded) ? loaded : []).filter((r) => r.useYn !== 'N');
      return {
        title: '사업 조회 (검색)',
        rows: rows.map((r) => ({ ...r, id: r.projectUid || r.projectCode })),
        columns: [
          hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
          hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 180 }),
        ],
        enableSearch: true,
        searchConfig: { mode: 'unified', keywordFields: ['projectCode', 'projectName'] },
      };
    }
    if (searchLookup.type === 'subject') {
      const rows = (loadSubjectsByFilter({ tenantCode, fiscalYear: fy, inoutDiv: '지출' }) || []).filter(
        (r) => r.useYn !== 'N' && r.mod !== 'D'
      );
      return {
        title: '과목 조회 (검색)',
        rows: rows.map((r) => ({
          ...r,
          id: r.subjectUid || r.subjectCode,
        })),
        columns: [
          hiCol.roTextColumn({ header: '과목코드', field: 'subjectDispCode', size: 110 }),
          hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 160 }),
        ],
        enableSearch: true,
        searchConfig: {
          mode: 'unified',
          keywordFields: ['subjectDispCode', 'subjectCode', 'subjectName'],
        },
      };
    }
    return { title: '', rows: [], columns: [], enableSearch: false };
  }, [searchLookup.type, tenantCode, searchFiscalYearWatch]);

  const applySearchLookup = useCallback(
    (payload) => {
      const rows = (Array.isArray(payload) ? payload : [payload]).filter(Boolean);
      const axis = {
        dept: {
          keys: ['searchDeptCode', 'searchDeptName', 'searchDeptDisp'],
          code: (r) => r.deptCode,
          name: (r) => r.deptName,
        },
        project: {
          keys: ['searchProjectCode', 'searchProjectName', 'searchProjectDisp'],
          code: (r) => r.projectCode,
          name: (r) => r.projectName,
        },
        subject: {
          keys: ['searchSubjectCode', 'searchSubjectName', 'searchSubjectDisp'],
          code: (r) => r.subjectDispCode || r.subjectCode,
          name: (r) => r.subjectName,
        },
      }[searchLookup.type];
      if (!axis) return true;

      const [codeKey, nameKey, dispKey] = axis.keys;
      // 빈 배열 = 조건 해제(전체 조회)
      if (!rows.length) {
        setValue(codeKey, '');
        setValue(nameKey, '');
        setValue(dispKey, '');
        return true;
      }

      const codes = rows.map((r) => String(axis.code(r) || '').trim()).filter(Boolean);
      const names = rows.map((r) => String(axis.name(r) || '').trim()).filter(Boolean);
      setValue(codeKey, codes.join(','));
      setValue(nameKey, names.join(','));
      setValue(dispKey, summarizeSelection(names));
      return true;
    },
    [searchLookup.type, setValue]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'searchFiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          parentWidth: SEARCH_SLOT_W[0],
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchStatus',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...EXPENSE_STATUS_OPTIONS],
          parentWidth: SEARCH_SLOT_W[1],
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchProposalDateRange',
          label: '품의일',
          type: 'dateRange',
          startFieldName: 'searchDateFrom',
          endFieldName: 'searchDateTo',
          parentWidth: SEARCH_SLOT_W[2],
          width: SEARCH_DATE_W,
          labelWidth: SEARCH_LABEL_W,
          fiscalYear: searchFiscalYearWatch || getCurrentFiscalYear(),
          presetSet: 'inquiry',
        },
      ],
      [
        {
          name: 'searchDeptDisp',
          label: '부서',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[0],
          width: '160px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '부서 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'dept' }),
          title: selectionTooltip(searchDeptNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchProjectDisp',
          label: '사업',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[1],
          width: '210px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '사업 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'project' }),
          title: selectionTooltip(searchProjectNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchSubjectDisp',
          label: '과목',
          type: 'text',
          parentWidth: SEARCH_SLOT_W[2],
          width: '210px',
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '과목 조회',
          placeholder: '전체',
          onSearch: () => setSearchLookup({ open: true, type: 'subject' }),
          title: selectionTooltip(searchSubjectNameWatch, '팝업에서 선택 · [조건 해제]로 전체'),
        },
        {
          name: 'searchRemark',
          label: '적요',
          type: 'text',
          width: '320px',
          labelWidth: SEARCH_LABEL_W,
          placeholder: '부분 일치',
        },
      ],
    ],
    [searchFiscalYearWatch, searchDeptNameWatch, searchProjectNameWatch, searchSubjectNameWatch]
  );

  const masterFields = useMemo(
    () => [
      [
        { name: 'proposalNo', label: '품의번호', type: 'text', readOnly: true, parentWidth: MASTER_SLOT_W[0], width: '112px', labelWidth: MASTER_LABEL_W },
        { name: 'deptName', label: '부서', type: 'text', readOnly: true, parentWidth: MASTER_SLOT_W[1], width: '150px', labelWidth: MASTER_LABEL_W },
        { name: 'projectName', label: '사업', type: 'text', readOnly: true, parentWidth: MASTER_SLOT_W[2], width: '210px', labelWidth: MASTER_LABEL_W },
        { name: 'subjectName', label: '과목', type: 'text', readOnly: true, parentWidth: MASTER_SLOT_W[3], width: '210px', labelWidth: MASTER_LABEL_W },
        { name: 'fundName', label: '재원', type: 'text', readOnly: true, parentWidth: MASTER_SLOT_W[4], width: '120px', labelWidth: MASTER_LABEL_W },
        { name: 'allocAmt', label: '현액', type: 'number', readOnly: true, parentWidth: MASTER_SLOT_W[5], width: '120px', labelWidth: MASTER_LABEL_W },
      ],
      [
        {
          name: 'proposalAmt',
          label: '품의금액',
          type: 'number',
          parentWidth: MASTER_SLOT_W[0],
          width: '130px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'proposalDate',
          label: '품의일',
          type: 'date',
          parentWidth: MASTER_SLOT_W[1],
          width: '140px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'proposalType',
          label: '품의유형',
          type: 'select',
          options: PROPOSAL_TYPE_OPTIONS,
          parentWidth: MASTER_SLOT_W[2],
          width: '150px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
        {
          name: 'statusNm',
          label: '상태',
          type: 'text',
          readOnly: true,
          parentWidth: MASTER_SLOT_W[3],
          width: '90px',
          labelWidth: MASTER_LABEL_W,
        },
        {
          // 재원·현액(짧은 2개 열) 자리를 합쳐 적요 한 필드로 사용
          name: 'remark',
          label: '적요',
          type: 'text',
          parentWidth: MASTER_SLOT_W_REMARK,
          width: '330px',
          labelWidth: MASTER_LABEL_W,
          readOnly: masterReadonly,
        },
      ],
    ],
    [masterReadonly]
  );

  // ─── 엑셀 (품의목록 그리드는 미등록 · 마스터 폼 + 거래처만) ───
  const getMasterExportData = useCallback(() => {
    const v = getValues();
    const uid = String(v.proposalUid || selectedUidRef.current || '').trim();
    if (!uid) return [];
    return [
      {
        proposalUid: uid,
        proposalNo: v.proposalNo || '',
        fiscalYear: v.fiscalYear || '',
        deptCode: v.deptCode || '',
        deptName: v.deptName || '',
        projectCode: v.projectCode || '',
        projectName: v.projectName || '',
        subjectCode: v.subjectCode || '',
        subjectName: v.subjectName || '',
        fundCode: v.fundCode || '',
        fundName: v.fundName || '',
        allocAmt: Number(v.allocAmt) || 0,
        proposalAmt: Number(v.proposalAmt) || 0,
        proposalDate: v.proposalDate || '',
        proposalType: v.proposalType || '',
        proposalTypeNm: proposalTypeLabel(v.proposalType),
        status: v.status || '',
        statusNm: expenseStatusLabel(v.status) || v.statusNm || '',
        remark: v.remark || '',
      },
    ];
  }, [getValues]);

  const getMasterExportCols = useCallback(
    () => [
      { header: '품의번호', accessorKey: 'proposalNo' },
      { header: '회계연도', accessorKey: 'fiscalYear' },
      { header: '부서코드', accessorKey: 'deptCode' },
      { header: '부서', accessorKey: 'deptName' },
      { header: '사업코드', accessorKey: 'projectCode' },
      { header: '사업', accessorKey: 'projectName' },
      { header: '과목코드', accessorKey: 'subjectCode' },
      { header: '과목', accessorKey: 'subjectName' },
      { header: '재원코드', accessorKey: 'fundCode' },
      { header: '재원', accessorKey: 'fundName' },
      { header: '현액', accessorKey: 'allocAmt' },
      { header: '품의금액', accessorKey: 'proposalAmt' },
      { header: '품의일', accessorKey: 'proposalDate' },
      { header: '품의유형', accessorKey: 'proposalTypeNm' },
      { header: '상태', accessorKey: 'statusNm' },
      { header: '적요', accessorKey: 'remark' },
    ],
    []
  );

  const getVendorExportData = useCallback(
    () =>
      (vendorRef.current?.getFilteredData?.() ?? vendorRef.current?.getData?.() ?? []).filter(
        (r) => r.mod !== 'D'
      ),
    []
  );
  const getVendorExportCols = useCallback(
    () => vendorRef.current?.getDataColumns?.() ?? vendorColumns,
    [vendorColumns]
  );

  useRegisterGridExport({
    id: MASTER_EXPORT_ID,
    label: '품의마스터',
    getData: getMasterExportData,
    getDataColumns: getMasterExportCols,
    joinKeys: ['proposalUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: VENDOR_GRID_ID,
    label: '거래처',
    getData: getVendorExportData,
    getDataColumns: getVendorExportCols,
    joinKeys: ['proposalUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: isApprove ? '지출품의승인' : '지출품의등록',
  });

  const actionButtons = useMemo(() => {
    const base = [
      {
        id: 'search',
        label: '조회',
        icon: '🔍',
        onClick: handleSearch,
        hotkey: 'F7',
        title: '조회 (F7)',
      },
    ];
    if (isApprove) return base;
    return [
      ...base,
      {
        id: 'new',
        label: '추가',
        icon: '➕',
        onClick: handleNew,
        hotkey: 'F8',
        title: '신규 품의 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
        title: '저장 (F10)',
      },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
        title: '엑셀 다운로드 (F11) · 품의목록 제외',
      },
    ];
  }, [isApprove, handleSearch, handleNew, handleActiveDelete, handleSave, openExcelDialog, activeLabel]);

  const extraButtons = useMemo(() => {
    if (isApprove) {
      return [
        { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
        { id: 'reject', label: '반려', onClick: handleReject, variant: 'danger' },
      ];
    }

    // 기타/사업소득일 때만 활성화 · 항상 노출 (불러오기와 동일 패턴)
    const isOtherIncome = proposalTypeWatch === PROPOSAL_TYPE.OTHER;
    const incomeDisabled = !isOtherIncome || !selectedUid;
    let incomeTitle = '소득명세 등록';
    if (!selectedUid) incomeTitle = '품의를 선택하거나 [추가] 후 등록하세요.';
    else if (!isOtherIncome) incomeTitle = '품의유형이 「기타/사업소득」일 때만 등록할 수 있습니다.';
    else if (masterReadonly) incomeTitle = '조회 전용으로 소득명세를 엽니다.';

    return [
      {
        id: 'incomeStmt',
        label: incomeStmtCount ? `소득명세(${incomeStmtCount})` : '소득명세',
        onClick: () => {
          if (!selectedUid) {
            showAlert('품의를 선택하거나 [추가] 후 소득명세를 등록하세요.');
            return;
          }
          if (proposalTypeWatch !== PROPOSAL_TYPE.OTHER) {
            showAlert('품의유형이 「기타/사업소득」일 때만 소득명세를 등록할 수 있습니다.');
            return;
          }
          setIncomeStmtOpen(true);
        },
        disabled: incomeDisabled,
        title: incomeTitle,
      },
      { id: 'request', label: '승인요청', onClick: handleRequest },
    ];
  }, [
    isApprove,
    handleApprove,
    handleReject,
    handleRequest,
    proposalTypeWatch,
    selectedUid,
    masterReadonly,
    incomeStmtCount,
  ]);

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack {...scrollbarHoverProps}>
          <CollapsibleSearchPanel
            storageKey={`expense-proposal-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            {isApprove
              ? '승인요청 건 선택 후 승인/반려'
              : '현액 불러오기 · 기타/사업소득 시 소득명세 · 엑셀(F11)은 품의목록 제외 · Alt+F2 그리드 전환'}
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
          </Hint>

          <Panel>
            <GridExportFocusScope gridId={MASTER_EXPORT_ID} style={{ flex: '0 0 auto' }}>
              <MasterToolbar>
                <strong style={{ fontSize: theme.typography.fontSize.sm }}>품의 마스터</strong>
                <BalanceBtn
                  type="button"
                  onClick={() => setBalanceLookupOpen(true)}
                  disabled={isApprove || masterReadonly}
                  title={
                    isApprove
                      ? '승인 화면에서는 불러올 수 없습니다.'
                      : masterReadonly
                        ? '작성중·반려 건만 불러올 수 있습니다. [추가]로 신규 작성하세요.'
                        : '현액 불러오기'
                  }
                >
                  불러오기 ···
                </BalanceBtn>
              </MasterToolbar>
              <FormArea dense fields={masterFields} />
            </GridExportFocusScope>
          </Panel>

          {/* 남는 공간은 차지하되(grow), 목록을 펼쳐도 줄어들지 않음(shrink 0) */}
          <Panel $flex="1 0 auto" $height={VENDOR_PANEL_H}>
            <GridExportFocusScope gridId={VENDOR_GRID_ID}>
              <ActiveGridScope
                gridId={VENDOR_GRID_ID}
                label="거래처"
                order={20}
                onAdd={dispatchVendorAdd}
                onDelete={async () => {
                  await vendorGrid.handleDelete?.();
                }}
                canAdd={!isApprove && !masterReadonly}
                canDelete={!isApprove && !masterReadonly}
              >
                <ActiveGridHeader gridId={VENDOR_GRID_ID}>거래처 디테일</ActiveGridHeader>
                <GridBody>
                  <HiGridComponent
                    ref={vendorRef}
                    columns={vendorColumns}
                    showRowNumber
                    rowNumberSize={48}
                    enableColumnFilters
                    enableContextMenu={!isApprove && !masterReadonly}
                    enableRowEditMenu={!isApprove && !masterReadonly}
                    enableCut={!isApprove && !masterReadonly}
                    enableFindReplace
                    allowReplace={!isApprove && !masterReadonly}
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    exportFileName="지출품의거래처"
                    gridId={`expense-proposal-vendor-${screenMode}`}
                    prefsUserId={prefsUserId}
                    headerActive={activeGridId === VENDOR_GRID_ID}
                    {...(isApprove || masterReadonly ? {} : vendorGrid.contextMenuProps || {})}
                  />
                </GridBody>
              </ActiveGridScope>
            </GridExportFocusScope>
          </Panel>

          <CollapsibleSectionPanel
            storageKey={`expense-proposal-list-${screenMode}`}
            prefsUserId={prefsUserId}
            title="품의 목록"
            summary={listCount ? `${listCount}건` : '조회 후 표시'}
            defaultCollapsed
            expandedHeight={LIST_PANEL_H}
            expandedMaxHeight="none"
          >
            <GridBody>
              <HiGridComponent
                ref={listRef}
                columns={listColumns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableHeaderMenu
                enableQuickExport={false}
                exportFileName="지출품의목록"
                gridId={`expense-proposal-list-${screenMode}`}
                prefsUserId={prefsUserId}
              />
            </GridBody>
          </CollapsibleSectionPanel>
        </Stack>
      </FormProvider>

      <CodeLookupDialog
        open={balanceLookupOpen}
        title={balanceLookupConfig.title}
        columns={balanceLookupConfig.columns}
        rows={balanceLookupConfig.rows}
        enableSearch={balanceLookupConfig.enableSearch}
        searchConfig={balanceLookupConfig.searchConfig}
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-expense-proposal-load"
        onSelect={applyBalance}
        onClose={() => setBalanceLookupOpen(false)}
      />

      <CodeLookupDialog
        open={searchLookup.open}
        title={searchLookupConfig.title}
        columns={searchLookupConfig.columns}
        rows={searchLookupConfig.rows}
        enableSearch={searchLookupConfig.enableSearch}
        searchConfig={searchLookupConfig.searchConfig}
        multiSelect
        prefsUserId={prefsUserId}
        sizeKey={`codeLookup-proposal-search-${searchLookup.type || 'x'}`}
        onSelect={applySearchLookup}
        onClose={() => setSearchLookup({ open: false, type: '' })}
      />

      <IncomeStatementDialog
        open={incomeStmtOpen}
        onClose={() => {
          setIncomeStmtOpen(false);
          refreshIncomeStmtCount();
        }}
        dims={{
          tenantCode,
          fiscalYear: String(searchFiscalYearWatch || getValues('fiscalYear') || '').trim(),
        }}
        proposalUid={selectedUid}
        proposalNo={proposalNoWatch || ''}
        proposalAmt={proposalAmtWatch || 0}
        readOnly={masterReadonly}
        prefsUserId={prefsUserId}
      />

      {excelDialog}
    </PageContainer>
  );
}

export default function ExpenseProposalWorkspace(props) {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <ExpenseProposalWorkspaceInner {...props} />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
